import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { PendingUsersBanner } from '../../components/PendingUsersBanner';
import {
  Project,
  TaskWithDetails,
  Trade,
  Story,
  Room,
  getProjects,
  getProjectTasks,
  getTradesAuth,
  getStories,
  getRooms,
  verifyTask,
  createTaskAuth,
  createTrade,
  uploadTaskPhoto,
  deleteTaskPhoto,
  getTaskPhotoUrl
} from '../../api/client';
import { EditTaskModal } from '../../components/EditTaskModal';
import { jsPDF } from 'jspdf';
import { levenshteinDistance, stem, getStemmedWords, hasWordOverlap } from '../../utils/fuzzyMatch';

type FilterStatus = 'all' | 'open' | 'done' | 'verified';
type GroupByOption = 'none' | 'room' | 'story' | 'trade';

interface NewTaskRow {
  roomSearch: string;
  selectedRoomId: number | null;
  description: string;
  tradeId: number | null;
  dueDate: string;
  photo: File | null;
  photoPreview: string | null;
}

interface ImportedTask {
  tempId: string;
  description: string;
  roomId: number | null;
  tradeId: number | null;
  dueDate: Date | null;
  status: 'open' | 'done' | 'verified';
  warnings: {
    roomNotFound?: string;
    tradeNotFound?: string;
  };
}

interface ImportSummary {
  imported: number;
  skipped: { row: number; reason: string }[];
  roomWarnings: number;
  tradeWarnings: number;
}

interface ImportMode {
  isActive: boolean;
  showPreUploadModal: boolean;
  showSummaryModal: boolean;
  tasks: ImportedTask[];
  summary: ImportSummary | null;
}

// Parse CSV date format (MM/DD/YYYY)
function parseCSVDate(dateStr: string): Date | null {
  if (!dateStr.trim()) return null;
  const match = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  const [, month, day, year] = match;
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  if (isNaN(date.getTime())) return null;
  return date;
}

// Fuzzy match trade by name
function matchTrade(input: string, trades: Trade[]): Trade | null {
  const normalized = input.toLowerCase().trim();
  if (!normalized) return null;

  let match = trades.find(t => t.name.toLowerCase() === normalized);
  if (match) return match;

  match = trades.find(t => t.name.toLowerCase().startsWith(normalized));
  if (match) return match;

  match = trades.find(t => t.name.toLowerCase().includes(normalized));
  if (match) return match;

  const inputWords = getStemmedWords(normalized);
  match = trades.find(t => hasWordOverlap(inputWords, getStemmedWords(t.name)));
  if (match) return match;

  match = trades.find(t => levenshteinDistance(t.name.toLowerCase(), normalized) <= 2);
  if (match) return match;

  return null;
}

// Match room by number or name
function matchRoom(roomNumber: string, roomName: string, rooms: Room[]): { room: Room | null; warning?: string } {
  const numTrimmed = roomNumber.trim();
  const nameTrimmed = roomName.trim();

  if (!numTrimmed && !nameTrimmed) {
    const wholeHouse = rooms.find(r => r.number === '0');
    if (wholeHouse) return { room: wholeHouse };
    return { room: null, warning: 'Whole House room (0) not found' };
  }

  if (numTrimmed) {
    const match = rooms.find(r => r.number.toLowerCase() === numTrimmed.toLowerCase());
    if (match) return { room: match };
    return { room: null, warning: `Room number "${numTrimmed}" not found` };
  }

  if (nameTrimmed) {
    const matches = rooms.filter(r => r.name?.toLowerCase() === nameTrimmed.toLowerCase());
    if (matches.length === 1) return { room: matches[0] };
    if (matches.length > 1) return { room: null, warning: `Multiple rooms named "${nameTrimmed}"` };
    return { room: null, warning: `Room name "${nameTrimmed}" not found` };
  }

  return { room: null };
}

// Parse CSV content into rows
function parseCSVContent(content: string): string[][] {
  const rows: string[][] = [];
  const lines = content.split(/\r?\n/);

  for (const line of lines) {
    if (!line.trim()) continue;

    const row: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        row.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    row.push(current.trim());
    rows.push(row);
  }

  return rows;
}

export function TaskManagement() {
  const { projectId } = useParams<{ projectId: string }>();
  const { token, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<TaskWithDetails[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [stories, setStories] = useState<Story[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('all');
  const [tradeFilter, setTradeFilter] = useState<number | null>(null);
  const [storyFilter, setStoryFilter] = useState<number | null>(null);
  const [groupBy, setGroupBy] = useState<GroupByOption>('none');
  const [sortColumn, setSortColumn] = useState<keyof TaskWithDetails>('room_number');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [verifyingTaskId, setVerifyingTaskId] = useState<number | null>(null);
  const [editingTask, setEditingTask] = useState<TaskWithDetails | null>(null);

  // New task row state
  const [newTask, setNewTask] = useState<NewTaskRow>({
    roomSearch: '',
    selectedRoomId: null,
    description: '',
    tradeId: null,
    dueDate: '',
    photo: null,
    photoPreview: null
  });
  const [showRoomDropdown, setShowRoomDropdown] = useState(false);
  const [showNewTaskTradeDropdown, setShowNewTaskTradeDropdown] = useState(false);
  const [tradeSearchText, setTradeSearchText] = useState('');
  const [addingTask, setAddingTask] = useState(false);

  // CSV Import state
  const [importMode, setImportMode] = useState<ImportMode | null>(null);

  const roomSearchRef = useRef<HTMLInputElement>(null);
  const roomDropdownRef = useRef<HTMLDivElement>(null);
  const newTaskTradeDropdownRef = useRef<HTMLDivElement>(null);

  // Photo management state
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [uploadingPhotoTaskId, setUploadingPhotoTaskId] = useState<number | null>(null);
  const newTaskPhotoInputRef = useRef<HTMLInputElement>(null);

  // Add new trade state
  const [newTradeName, setNewTradeName] = useState('');
  const [addingNewTrade, setAddingNewTrade] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }

    const loadData = async () => {
      if (!token || !projectId) return;
      try {
        const [projects, tasksData, tradesData, storiesData, roomsData] = await Promise.all([
          getProjects(token),
          getProjectTasks(token, parseInt(projectId)),
          getTradesAuth(token),
          getStories(token, parseInt(projectId)).catch(() => []),
          getRooms(token, parseInt(projectId))
        ]);

        const found = projects.find(p => p.id === parseInt(projectId));
        if (found) {
          setProject(found);
          setTasks(tasksData);
          setTrades(tradesData);
          setStories(storiesData);
          setRooms(roomsData);
        } else {
          navigate('/admin');
        }
      } catch (error) {
        console.error('Failed to load data:', error);
        navigate('/admin');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId, token, isAuthenticated, navigate]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (roomDropdownRef.current && !roomDropdownRef.current.contains(e.target as Node) &&
          roomSearchRef.current && !roomSearchRef.current.contains(e.target as Node)) {
        setShowRoomDropdown(false);
      }
      if (newTaskTradeDropdownRef.current && !newTaskTradeDropdownRef.current.contains(e.target as Node)) {
        setShowNewTaskTradeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredTasks = tasks.filter(task => {
    if (statusFilter !== 'all' && task.status !== statusFilter) return false;
    if (tradeFilter !== null && task.trade_id !== tradeFilter) return false;
    if (storyFilter !== null && task.story_id !== storyFilter) return false;
    return true;
  });

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    if (a.status === 'done' && b.status !== 'done') return -1;
    if (a.status !== 'done' && b.status === 'done') return 1;

    const aValue = a[sortColumn];
    const bValue = b[sortColumn];

    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    let comparison = 0;
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      comparison = aValue.localeCompare(bValue, undefined, { numeric: true });
    } else if (typeof aValue === 'number' && typeof bValue === 'number') {
      comparison = aValue - bValue;
    } else {
      comparison = String(aValue).localeCompare(String(bValue));
    }

    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const groupedTasks = () => {
    if (groupBy === 'none') {
      return { 'All Tasks': sortedTasks };
    }

    const groups: Record<string, TaskWithDetails[]> = {};

    sortedTasks.forEach(task => {
      let key = 'Uncategorized';
      if (groupBy === 'room') {
        key = `Room ${task.room_number}${task.room_name ? ' - ' + task.room_name : ''}`;
      } else if (groupBy === 'story') {
        key = task.story_name || 'No Story Assigned';
      } else if (groupBy === 'trade') {
        key = task.trade_name || 'No Trade Assigned';
      }

      if (!groups[key]) groups[key] = [];
      groups[key].push(task);
    });

    return groups;
  };

  const handleSort = (column: keyof TaskWithDetails) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Room search filtering
  const filteredRooms = useMemo(() => {
    if (!newTask.roomSearch.trim()) return rooms;
    const search = newTask.roomSearch.toLowerCase().trim();
    return rooms.filter(r =>
      r.number.toLowerCase().includes(search) ||
      (r.name && r.name.toLowerCase().includes(search))
    );
  }, [rooms, newTask.roomSearch]);

  // Handle room selection
  const handleRoomSelect = (room: Room) => {
    setNewTask(prev => ({
      ...prev,
      roomSearch: `${room.number}${room.name ? ' - ' + room.name : ''}`,
      selectedRoomId: room.id
    }));
    setShowRoomDropdown(false);
  };

  // Handle room search change
  const handleRoomSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewTask(prev => ({ ...prev, roomSearch: value, selectedRoomId: null }));
    setShowRoomDropdown(true);

    const exactMatch = rooms.find(r => r.number.toLowerCase() === value.toLowerCase().trim());
    if (exactMatch) {
      setNewTask(prev => ({
        ...prev,
        roomSearch: `${exactMatch.number}${exactMatch.name ? ' - ' + exactMatch.name : ''}`,
        selectedRoomId: exactMatch.id
      }));
      setShowRoomDropdown(false);
    }
  };

  // Add new task
  const handleAddTask = async () => {
    if (!token || !newTask.selectedRoomId || !newTask.description.trim()) return;
    setAddingTask(true);
    try {
      const createdTask = await createTaskAuth(
        token,
        newTask.selectedRoomId,
        newTask.description.trim(),
        newTask.tradeId,
        user?.username || null,
        newTask.dueDate ? newTask.dueDate + 'T00:00:00Z' : null
      );
      if (newTask.photo && createdTask.id) {
        await uploadTaskPhoto(createdTask.id, newTask.photo, token);
      }
      const tasksData = await getProjectTasks(token, parseInt(projectId!));
      setTasks(tasksData);
      setNewTask({ roomSearch: '', selectedRoomId: null, description: '', tradeId: null, dueDate: '', photo: null, photoPreview: null });
      setTradeSearchText('');
    } catch (error) {
      console.error('Failed to add task:', error);
      alert('Failed to add task. Please try again.');
    } finally {
      setAddingTask(false);
    }
  };

  // Handle new task photo selection
  const handleNewTaskPhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
    if (!validTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.heic')) {
      alert('Please select a valid image file (JPG, PNG, WebP, or HEIC)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('Photo must be less than 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setNewTask(prev => ({
        ...prev,
        photo: file,
        photoPreview: e.target?.result as string
      }));
    };
    reader.readAsDataURL(file);
  };

  // Handle task photo upload (for existing tasks)
  const handleTaskPhotoUpload = async (taskId: number, file: File) => {
    if (!token) return;
    setUploadingPhotoTaskId(taskId);
    try {
      await uploadTaskPhoto(taskId, file, token);
      const tasksData = await getProjectTasks(token, parseInt(projectId!));
      setTasks(tasksData);
    } catch (error) {
      console.error('Failed to upload photo:', error);
      alert('Failed to upload photo. Please try again.');
    } finally {
      setUploadingPhotoTaskId(null);
    }
  };

  // Handle task photo delete
  const handleDeleteTaskPhoto = async (taskId: number) => {
    if (!token) return;
    try {
      await deleteTaskPhoto(token, taskId);
      const tasksData = await getProjectTasks(token, parseInt(projectId!));
      setTasks(tasksData);
    } catch (error) {
      console.error('Failed to delete photo:', error);
      alert('Failed to delete photo. Please try again.');
    }
  };

  // Add new trade
  const handleAddNewTrade = async () => {
    if (!token || !newTradeName.trim()) return;
    setAddingNewTrade(true);
    try {
      const trade = await createTrade(token, newTradeName.trim());
      setTrades(prev => [...prev, trade]);
      setNewTradeName('');

      if (showNewTaskTradeDropdown) {
        setNewTask(prev => ({ ...prev, tradeId: trade.id }));
        setTradeSearchText('');
        setShowNewTaskTradeDropdown(false);
      }
    } catch (error) {
      console.error('Failed to add trade:', error);
      alert('Failed to add trade. Please try again.');
    } finally {
      setAddingNewTrade(false);
    }
  };

  const handleVerifyTask = async (taskId: number) => {
    if (!token) return;
    setVerifyingTaskId(taskId);
    try {
      await verifyTask(token, taskId);
      const tasksData = await getProjectTasks(token, parseInt(projectId!));
      setTasks(tasksData);
    } catch (error) {
      console.error('Failed to verify task:', error);
      alert('Failed to verify task. Please try again.');
    } finally {
      setVerifyingTaskId(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return '#dc3545';
      case 'done': return '#ffc107';
      case 'verified': return '#28a745';
      default: return '#6c757d';
    }
  };

  const exportToCSV = () => {
    const headers = ['Room', 'Description', 'Assigned To', 'Due Date', 'Status', 'Completed By', 'Story'];
    const rows = sortedTasks.map(task => [
      `Room ${task.room_number}${task.room_name ? ' - ' + task.room_name : ''}`,
      task.description,
      task.trade_name || 'N/A',
      task.due_date ? new Date(task.due_date).toLocaleDateString() : 'N/A',
      task.status,
      task.verified_by || 'N/A',
      task.story_name || 'N/A'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${project?.name || 'project'}-tasks-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    let yPosition = margin;

    doc.setFontSize(18);
    doc.text(project?.name || 'Project', margin, yPosition);
    yPosition += 10;

    doc.setFontSize(14);
    doc.text('Work-to-Complete Report', margin, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, margin, yPosition);
    yPosition += 8;

    doc.text(`Total Tasks: ${sortedTasks.length}`, margin, yPosition);
    yPosition += 5;
    doc.text(`Open: ${sortedTasks.filter(t => t.status === 'open').length}`, margin, yPosition);
    yPosition += 5;
    doc.text(`Done: ${sortedTasks.filter(t => t.status === 'done').length}`, margin, yPosition);
    yPosition += 5;
    doc.text(`Verified: ${sortedTasks.filter(t => t.status === 'verified').length}`, margin, yPosition);
    yPosition += 10;

    doc.setFontSize(9);

    const groups = groupedTasks();
    Object.entries(groups).forEach(([groupName, groupTasks]) => {
      if (yPosition > pageHeight - 20) {
        doc.addPage();
        yPosition = margin;
      }

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(groupName, margin, yPosition);
      yPosition += 7;

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');

      groupTasks.forEach(task => {
        const roomText = `Room ${task.room_number}${task.room_name ? ' - ' + task.room_name : ''}`;
        const maxWidth = pageWidth - 2 * margin;
        const descLines = doc.splitTextToSize(task.description, maxWidth - 20);
        const lineHeight = 5;
        const taskHeight = descLines.length * lineHeight + 3;

        if (yPosition + taskHeight > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
        }

        doc.setFont('helvetica', 'bold');
        doc.text(roomText, margin, yPosition);
        yPosition += lineHeight;

        doc.setFont('helvetica', 'normal');
        descLines.forEach((line: string) => {
          doc.text(line, margin + 5, yPosition);
          yPosition += lineHeight;
        });

        const statusColor = getStatusColor(task.status);
        doc.setTextColor(statusColor);
        doc.text(`Status: ${task.status.toUpperCase()}`, margin + 5, yPosition);
        doc.setTextColor(0, 0, 0);

        if (task.trade_name) {
          doc.text(`Trade: ${task.trade_name}`, margin + 50, yPosition);
        }
        yPosition += lineHeight + 2;
      });

      yPosition += 3;
    });

    doc.save(`${project?.name || 'project'}-tasks-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const downloadSampleCSV = () => {
    const headers = 'Room Number,Room Name,Description,Trade,Due Date,Status\n';
    const blob = new Blob([headers], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'wtc_import_sample.csv';
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const processCSVFile = (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      alert('File too large. Maximum size is 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const rows = parseCSVContent(content);

      if (rows.length < 2) {
        alert('CSV file is empty or contains only headers.');
        return;
      }

      const headers = rows[0].map(h => h.toLowerCase());
      const expectedHeaders = ['room number', 'room name', 'description', 'trade', 'due date', 'status'];
      const headersMatch = expectedHeaders.every((h, i) => headers[i]?.includes(h.split(' ')[0]));

      if (!headersMatch) {
        alert('Invalid CSV format. Please use the sample CSV template.');
        return;
      }

      const importedTasks: ImportedTask[] = [];
      const skipped: { row: number; reason: string }[] = [];
      let roomWarnings = 0;
      let tradeWarnings = 0;

      const dataRows = rows.slice(1);
      const maxRows = Math.min(dataRows.length, 500);

      if (dataRows.length > 500) {
        alert(`Import limited to 500 tasks. Only first 500 of ${dataRows.length} rows will be processed.`);
      }

      for (let i = 0; i < maxRows; i++) {
        const row = dataRows[i];
        const rowNumber = i + 2;

        const [roomNumber = '', roomName = '', description = '', tradeName = '', dueDateStr = '', statusStr = ''] = row;

        if (!description.trim()) {
          skipped.push({ row: rowNumber, reason: 'No description' });
          continue;
        }

        const roomResult = matchRoom(roomNumber, roomName, rooms);
        const warnings: ImportedTask['warnings'] = {};

        if (roomResult.warning) {
          warnings.roomNotFound = roomResult.warning;
          roomWarnings++;
        }

        let tradeId: number | null = null;
        if (tradeName.trim()) {
          const matchedTrade = matchTrade(tradeName, trades);
          if (matchedTrade) {
            tradeId = matchedTrade.id;
          } else {
            warnings.tradeNotFound = tradeName.trim();
            tradeWarnings++;
          }
        }

        const dueDate = parseCSVDate(dueDateStr);

        const statusLower = statusStr.toLowerCase().trim();
        const status: 'open' | 'done' | 'verified' =
          statusLower === 'done' ? 'done' :
          statusLower === 'verified' ? 'verified' : 'open';

        importedTasks.push({
          tempId: `import-${Date.now()}-${i}`,
          description: description.trim(),
          roomId: roomResult.room?.id || null,
          tradeId,
          dueDate,
          status,
          warnings
        });
      }

      setImportMode({
        isActive: false,
        showPreUploadModal: false,
        showSummaryModal: true,
        tasks: importedTasks,
        summary: {
          imported: importedTasks.length,
          skipped,
          roomWarnings,
          tradeWarnings
        }
      });
    };

    reader.onerror = () => {
      alert('Failed to read CSV file. Please try again.');
    };

    reader.readAsText(file);
  };

  const handleCancelImport = () => {
    setImportMode(null);
  };

  // Helper to get room display for imported tasks
  const getRoomDisplayForImport = (task: ImportedTask) => {
    if (task.roomId) {
      const room = rooms.find(r => r.id === task.roomId);
      return room ? { number: room.number, name: room.name } : { number: '?', name: undefined };
    }
    return { number: '?', name: task.warnings.roomNotFound };
  };

  // Update imported task
  const updateImportedTask = (tempId: string, updates: Partial<ImportedTask>) => {
    setImportMode(prev => {
      if (!prev) return null;
      return {
        ...prev,
        tasks: prev.tasks.map(t =>
          t.tempId === tempId ? { ...t, ...updates } : t
        )
      };
    });
  };

  // Trade dropdown component for reuse
  const TradeDropdown = ({
    value,
    onChange,
    searchText,
    onSearchChange,
    isOpen,
    onToggle,
    dropdownId
  }: {
    value: number | null;
    onChange: (tradeId: number | null) => void;
    searchText: string;
    onSearchChange: (text: string) => void;
    isOpen: boolean;
    onToggle: () => void;
    dropdownId: string;
  }) => {
    const selectedTrade = trades.find(t => t.id === value);
    const filtered = trades.filter(t =>
      t.name.toLowerCase().includes(searchText.toLowerCase())
    );

    return (
      <div style={{ position: 'relative' }}>
        <button
          id={`trade-button-${dropdownId}`}
          onClick={onToggle}
          style={{
            padding: '6px 10px',
            fontSize: '13px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            background: '#fff',
            cursor: 'pointer',
            minWidth: '140px',
            textAlign: 'left',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span style={{ color: selectedTrade ? '#333' : '#999' }}>
            {selectedTrade?.name || 'Select trade...'}
          </span>
          <span style={{ marginLeft: '8px', fontSize: '10px' }}>▼</span>
        </button>

        {isOpen && (
          <div
            id={`trade-dropdown-${dropdownId}`}
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              minWidth: '200px',
              background: '#fff',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 1000,
              marginTop: '2px'
            }}
          >
            <input
              id={`trade-search-${dropdownId}`}
              type="text"
              value={searchText}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && filtered.length > 0) {
                  e.preventDefault();
                  onChange(filtered[0].id);
                  onToggle();
                  onSearchChange('');
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  onToggle();
                  onSearchChange('');
                } else if (e.key === 'ArrowDown' && filtered.length > 0) {
                  e.preventDefault();
                  const firstOption = document.getElementById(`trade-option-${dropdownId}-${filtered[0].id}`);
                  firstOption?.focus();
                }
              }}
              placeholder="Type to search, press Enter to select..."
              autoFocus
              style={{
                width: '100%',
                padding: '8px',
                border: 'none',
                borderBottom: '1px solid #eee',
                fontSize: '13px',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              <div
                id={`trade-option-${dropdownId}-none`}
                tabIndex={0}
                role="button"
                onClick={() => { onChange(null); onToggle(); onSearchChange(''); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(null);
                    onToggle();
                    onSearchChange('');
                  } else if (e.key === 'ArrowDown' && filtered.length > 0) {
                    e.preventDefault();
                    const firstOption = document.getElementById(`trade-option-${dropdownId}-${filtered[0].id}`);
                    firstOption?.focus();
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    const searchInput = document.getElementById(`trade-search-${dropdownId}`);
                    searchInput?.focus();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    onToggle();
                    onSearchChange('');
                  }
                }}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#999',
                  fontStyle: 'italic',
                  borderBottom: '1px solid #eee',
                  outline: 'none'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                onFocus={(e) => e.currentTarget.style.background = '#f5f5f5'}
                onBlur={(e) => e.currentTarget.style.background = 'transparent'}
              >
                No trade assigned
              </div>
              {filtered.map((trade, index) => (
                <div
                  key={trade.id}
                  id={`trade-option-${dropdownId}-${trade.id}`}
                  tabIndex={0}
                  role="button"
                  aria-pressed={value === trade.id}
                  onClick={() => { onChange(trade.id); onToggle(); onSearchChange(''); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onChange(trade.id);
                      onToggle();
                      onSearchChange('');
                    } else if (e.key === 'ArrowDown' && index < filtered.length - 1) {
                      e.preventDefault();
                      const nextOption = document.getElementById(`trade-option-${dropdownId}-${filtered[index + 1].id}`);
                      nextOption?.focus();
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      if (index === 0) {
                        const searchInput = document.getElementById(`trade-search-${dropdownId}`);
                        searchInput?.focus();
                      } else {
                        const prevOption = document.getElementById(`trade-option-${dropdownId}-${filtered[index - 1].id}`);
                        prevOption?.focus();
                      }
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      onToggle();
                      onSearchChange('');
                    }
                  }}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    background: value === trade.id ? '#e3f2fd' : 'transparent',
                    outline: 'none',
                    borderLeft: value === trade.id ? '3px solid #1976d2' : '3px solid transparent'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = value === trade.id ? '#e3f2fd' : '#f5f5f5'}
                  onMouseLeave={(e) => e.currentTarget.style.background = value === trade.id ? '#e3f2fd' : 'transparent'}
                  onFocus={(e) => e.currentTarget.style.background = value === trade.id ? '#e3f2fd' : '#f5f5f5'}
                  onBlur={(e) => e.currentTarget.style.background = value === trade.id ? '#e3f2fd' : 'transparent'}
                >
                  {trade.name}
                </div>
              ))}
            </div>
            <div style={{ borderTop: '1px solid #eee', padding: '8px' }}>
              {!addingNewTrade ? (
                <button
                  onClick={() => {
                    setNewTradeName(searchText);
                    setAddingNewTrade(true);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px',
                    background: '#28a745',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 'bold'
                  }}
                >
                  + Add New Trade
                </button>
              ) : (
                <div style={{ display: 'flex', gap: '4px' }}>
                  <input
                    type="text"
                    value={newTradeName}
                    onChange={(e) => setNewTradeName(e.target.value)}
                    placeholder="Trade name..."
                    autoFocus
                    style={{
                      flex: 1,
                      padding: '6px',
                      fontSize: '13px',
                      border: '1px solid #ccc',
                      borderRadius: '4px'
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddNewTrade();
                      if (e.key === 'Escape') setAddingNewTrade(false);
                    }}
                  />
                  <button
                    onClick={handleAddNewTrade}
                    disabled={!newTradeName.trim()}
                    style={{
                      padding: '6px 12px',
                      background: '#28a745',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: newTradeName.trim() ? 'pointer' : 'not-allowed',
                      opacity: newTradeName.trim() ? 1 : 0.6
                    }}
                  >
                    Add
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!isAuthenticated || loading) {
    return (
      <div style={{ fontFamily: 'system-ui', padding: '24px' }}>
        Loading...
      </div>
    );
  }

  if (!project) {
    return null;
  }

  const grouped = groupedTasks();

  return (
    <div style={{ fontFamily: 'system-ui' }}>
      <header style={{
        padding: '16px 24px',
        background: '#1a1a2e',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate(`/admin/projects/${projectId}`)}
            style={{
              background: 'transparent',
              border: '1px solid #fff',
              color: '#fff',
              padding: '8px 16px',
              cursor: 'pointer',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            ← Back to Project
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '20px' }}>
              Work-to-Complete Management
            </h1>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', opacity: 0.8 }}>
              {project.name}
            </p>
          </div>
        </div>

        <button
          onClick={() => setImportMode({
            isActive: false,
            showPreUploadModal: true,
            showSummaryModal: false,
            tasks: [],
            summary: null
          })}
          disabled={importMode?.isActive}
          style={{
            background: '#17a2b8',
            color: '#fff',
            border: 'none',
            padding: '10px 20px',
            cursor: importMode?.isActive ? 'not-allowed' : 'pointer',
            borderRadius: '4px',
            fontSize: '14px',
            fontWeight: 'bold',
            opacity: importMode?.isActive ? 0.6 : 1
          }}
        >
          Import CSV
        </button>

        {importMode?.isActive && (
          <button
            onClick={handleCancelImport}
            style={{
              background: '#dc3545',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              cursor: 'pointer',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            Cancel Import
          </button>
        )}
      </header>

      <PendingUsersBanner />

      <main style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        {!importMode?.isActive && (
          <>
            {/* Filters and Controls */}
            <div style={{
              background: '#f8f9fa',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '16px'
              }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}>
                    Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as FilterStatus)}
                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                  >
                    <option value="all">All</option>
                    <option value="open">Open</option>
                    <option value="done">Done</option>
                    <option value="verified">Verified</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}>
                    Trade
                  </label>
                  <select
                    value={tradeFilter === null ? '' : tradeFilter}
                    onChange={(e) => setTradeFilter(e.target.value ? parseInt(e.target.value) : null)}
                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                  >
                    <option value="">All Trades</option>
                    {trades.map(trade => (
                      <option key={trade.id} value={trade.id}>{trade.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}>
                    Story
                  </label>
                  <select
                    value={storyFilter === null ? '' : storyFilter}
                    onChange={(e) => setStoryFilter(e.target.value ? parseInt(e.target.value) : null)}
                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                  >
                    <option value="">All Stories</option>
                    {stories.map(story => (
                      <option key={story.id} value={story.id}>{story.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}>
                    Group By
                  </label>
                  <select
                    value={groupBy}
                    onChange={(e) => setGroupBy(e.target.value as GroupByOption)}
                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                  >
                    <option value="none">No Grouping</option>
                    <option value="room">Room</option>
                    <option value="story">Story</option>
                    <option value="trade">Trade</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  onClick={exportToCSV}
                  style={{
                    background: '#28a745',
                    color: '#fff',
                    border: 'none',
                    padding: '10px 20px',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  Export to CSV
                </button>
                <button
                  onClick={exportToPDF}
                  style={{
                    background: '#dc3545',
                    color: '#fff',
                    border: 'none',
                    padding: '10px 20px',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  Export to PDF
                </button>
                <div style={{
                  marginLeft: 'auto',
                  padding: '10px 16px',
                  background: '#fff',
                  borderRadius: '4px',
                  fontWeight: 'bold'
                }}>
                  {sortedTasks.length} task{sortedTasks.length !== 1 ? 's' : ''} found
                </div>
              </div>
            </div>

            {/* New Task Row */}
            <div style={{
              background: '#e8f5e9',
              padding: '16px 20px',
              borderRadius: '8px',
              marginBottom: '24px',
              border: '2px dashed #4caf50'
            }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#2e7d32' }}>
                Add New Task
              </h3>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {/* Room search */}
                <div style={{ position: 'relative', flex: '0 0 200px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 'bold' }}>
                    Room
                  </label>
                  <input
                    ref={roomSearchRef}
                    type="text"
                    value={newTask.roomSearch}
                    onChange={handleRoomSearchChange}
                    onFocus={() => setShowRoomDropdown(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown' && filteredRooms.length > 0) {
                        e.preventDefault();
                        const firstOption = document.getElementById(`room-option-${filteredRooms[0].id}`);
                        firstOption?.focus();
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        setShowRoomDropdown(false);
                      }
                    }}
                    placeholder="Type room # or name..."
                    style={{
                      width: '100%',
                      padding: '8px',
                      fontSize: '14px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      boxSizing: 'border-box'
                    }}
                  />
                  {showRoomDropdown && filteredRooms.length > 0 && (
                    <div
                      ref={roomDropdownRef}
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        marginTop: '2px'
                      }}
                    >
                      {filteredRooms.map((room, index) => (
                        <div
                          key={room.id}
                          id={`room-option-${room.id}`}
                          tabIndex={0}
                          role="button"
                          onClick={() => handleRoomSelect(room)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleRoomSelect(room);
                            } else if (e.key === 'ArrowDown' && index < filteredRooms.length - 1) {
                              e.preventDefault();
                              const nextOption = document.getElementById(`room-option-${filteredRooms[index + 1].id}`);
                              nextOption?.focus();
                            } else if (e.key === 'ArrowUp') {
                              e.preventDefault();
                              if (index === 0) {
                                roomSearchRef.current?.focus();
                              } else {
                                const prevOption = document.getElementById(`room-option-${filteredRooms[index - 1].id}`);
                                prevOption?.focus();
                              }
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              setShowRoomDropdown(false);
                            }
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            outline: 'none'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          onFocus={(e) => e.currentTarget.style.background = '#f5f5f5'}
                          onBlur={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <strong>{room.number}</strong>
                          {room.name && <span style={{ color: '#666' }}> - {room.name}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Description */}
                <div style={{ flex: '1 1 300px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 'bold' }}>
                    Description
                  </label>
                  <input
                    type="text"
                    value={newTask.description}
                    onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter task description..."
                    style={{
                      width: '100%',
                      padding: '8px',
                      fontSize: '14px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Trade dropdown */}
                <div style={{ flex: '0 0 180px', position: 'relative' }} ref={newTaskTradeDropdownRef}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 'bold' }}>
                    Assigned To
                  </label>
                  <TradeDropdown
                    value={newTask.tradeId}
                    onChange={(tradeId) => setNewTask(prev => ({ ...prev, tradeId }))}
                    searchText={tradeSearchText}
                    onSearchChange={setTradeSearchText}
                    isOpen={showNewTaskTradeDropdown}
                    onToggle={() => setShowNewTaskTradeDropdown(!showNewTaskTradeDropdown)}
                    dropdownId="new-task"
                  />
                </div>

                {/* Due Date */}
                <div style={{ flex: '0 0 140px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 'bold' }}>
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={newTask.dueDate}
                    onChange={(e) => setNewTask(prev => ({ ...prev, dueDate: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '8px',
                      fontSize: '14px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Photo */}
                <div style={{ flex: '0 0 100px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 'bold' }}>
                    Photo
                  </label>
                  {newTask.photoPreview ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          border: '1px solid #ddd'
                        }}
                      >
                        <img
                          src={newTask.photoPreview}
                          alt="Preview"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                      <button
                        onClick={() => {
                          setNewTask(prev => ({ ...prev, photo: null, photoPreview: null }));
                          if (newTaskPhotoInputRef.current) {
                            newTaskPhotoInputRef.current.value = '';
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc3545',
                          cursor: 'pointer',
                          fontSize: '16px',
                          padding: 0
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <label style={{ cursor: 'pointer' }}>
                      <input
                        ref={newTaskPhotoInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/heic,.heic"
                        style={{ display: 'none' }}
                        onChange={handleNewTaskPhotoSelect}
                      />
                      <span style={{
                        display: 'inline-block',
                        padding: '8px 12px',
                        background: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '13px',
                        color: '#666'
                      }}>
                        + Add
                      </span>
                    </label>
                  )}
                </div>

                {/* Add button */}
                <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                  <button
                    onClick={handleAddTask}
                    disabled={addingTask || !newTask.selectedRoomId || !newTask.description.trim()}
                    style={{
                      padding: '8px 20px',
                      background: '#4caf50',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: (addingTask || !newTask.selectedRoomId || !newTask.description.trim()) ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      opacity: (addingTask || !newTask.selectedRoomId || !newTask.description.trim()) ? 0.6 : 1
                    }}
                  >
                    {addingTask ? 'Adding...' : 'Add Task'}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Tasks Display */}
        {importMode?.isActive ? (
          <div>
            <div style={{
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span style={{ fontWeight: 'bold', color: '#856404' }}>
                Import Preview: {importMode.tasks.length} task{importMode.tasks.length !== 1 ? 's' : ''} ready to save
              </span>
              <span style={{ fontSize: '13px', color: '#856404' }}>
                Click "Save Changes" to import, or "Cancel Import" to discard
              </span>
            </div>

            <div style={{
              background: '#fff',
              borderRadius: '8px',
              overflow: 'visible',
              border: '1px solid #ddd'
            }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', borderBottom: '2px solid #ddd', width: '120px' }}>Room</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', borderBottom: '2px solid #ddd' }}>Description</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', borderBottom: '2px solid #ddd', width: '160px' }}>Assigned To</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', borderBottom: '2px solid #ddd', width: '120px' }}>Due Date</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', borderBottom: '2px solid #ddd', width: '110px' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold', borderBottom: '2px solid #ddd', width: '40px' }}>Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {importMode.tasks.map(task => {
                    const hasWarnings = task.warnings.roomNotFound || task.warnings.tradeNotFound;
                    const roomDisplay = getRoomDisplayForImport(task);

                    return (
                      <tr
                        key={task.tempId}
                        style={{
                          borderBottom: '1px solid #eee',
                          background: hasWarnings ? '#FEF3C7' : 'transparent',
                          borderLeft: hasWarnings ? '4px solid #F59E0B' : 'none'
                        }}
                      >
                        <td style={{ padding: '12px' }}>
                          {task.roomId ? (
                            <div>
                              <div style={{ fontWeight: 'bold' }}>{roomDisplay.number}</div>
                              {roomDisplay.name && <div style={{ fontSize: '12px', color: '#666' }}>{roomDisplay.name}</div>}
                            </div>
                          ) : (
                            <div>
                              <select
                                value=""
                                onChange={(e) => {
                                  const roomId = parseInt(e.target.value);
                                  updateImportedTask(task.tempId, {
                                    roomId,
                                    warnings: { ...task.warnings, roomNotFound: undefined }
                                  });
                                }}
                                style={{
                                  padding: '4px',
                                  fontSize: '12px',
                                  border: '1px solid #F59E0B',
                                  borderRadius: '4px',
                                  background: '#fff',
                                  width: '100%'
                                }}
                              >
                                <option value="">Select room...</option>
                                {rooms.map(r => (
                                  <option key={r.id} value={r.id}>
                                    {r.number}{r.name ? ` - ${r.name}` : ''}
                                  </option>
                                ))}
                              </select>
                              <div style={{ fontSize: '11px', color: '#dc3545', marginTop: '4px' }}>
                                {task.warnings.roomNotFound}
                              </div>
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <input
                            type="text"
                            value={task.description}
                            onChange={(e) => updateImportedTask(task.tempId, { description: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              fontSize: '14px',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              boxSizing: 'border-box'
                            }}
                          />
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          {task.tradeId || !task.warnings.tradeNotFound ? (
                            <select
                              value={task.tradeId || ''}
                              onChange={(e) => updateImportedTask(task.tempId, {
                                tradeId: e.target.value ? parseInt(e.target.value) : null,
                                warnings: { ...task.warnings, tradeNotFound: undefined }
                              })}
                              style={{
                                padding: '6px 8px',
                                fontSize: '13px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                width: '100%'
                              }}
                            >
                              <option value="">No trade</option>
                              {trades.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                              ))}
                            </select>
                          ) : (
                            <div>
                              <select
                                value=""
                                onChange={(e) => {
                                  const tradeId = e.target.value ? parseInt(e.target.value) : null;
                                  updateImportedTask(task.tempId, {
                                    tradeId,
                                    warnings: { ...task.warnings, tradeNotFound: undefined }
                                  });
                                }}
                                style={{
                                  padding: '4px',
                                  fontSize: '12px',
                                  border: '1px solid #F59E0B',
                                  borderRadius: '4px',
                                  background: '#fff',
                                  width: '100%'
                                }}
                              >
                                <option value="">Select trade...</option>
                                {trades.map(t => (
                                  <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                              </select>
                              <div style={{ fontSize: '11px', color: '#dc3545', marginTop: '4px' }}>
                                Not found: "{task.warnings.tradeNotFound}"
                              </div>
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <input
                            type="date"
                            value={task.dueDate ? task.dueDate.toISOString().split('T')[0] : ''}
                            onChange={(e) => updateImportedTask(task.tempId, {
                              dueDate: e.target.value ? new Date(e.target.value) : null
                            })}
                            style={{
                              padding: '6px 8px',
                              fontSize: '13px',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              width: '100%',
                              boxSizing: 'border-box'
                            }}
                          />
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <select
                            value={task.status}
                            onChange={(e) => updateImportedTask(task.tempId, {
                              status: e.target.value as 'open' | 'done' | 'verified'
                            })}
                            style={{
                              padding: '6px 8px',
                              fontSize: '13px',
                              borderRadius: '4px',
                              border: '1px solid #ccc',
                              background: getStatusColor(task.status),
                              color: '#fff',
                              fontWeight: 'bold',
                              cursor: 'pointer'
                            }}
                          >
                            <option value="open" style={{ background: '#fff', color: '#333' }}>Open</option>
                            <option value="done" style={{ background: '#fff', color: '#333' }}>Done</option>
                            <option value="verified" style={{ background: '#fff', color: '#333' }}>Verified</option>
                          </select>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <button
                            onClick={() => {
                              setImportMode(prev => {
                                if (!prev) return null;
                                return {
                                  ...prev,
                                  tasks: prev.tasks.filter(t => t.tempId !== task.tempId)
                                };
                              });
                            }}
                            style={{
                              background: '#dc3545',
                              color: '#fff',
                              border: 'none',
                              padding: '6px 10px',
                              cursor: 'pointer',
                              borderRadius: '4px',
                              fontSize: '14px',
                              fontWeight: 'bold'
                            }}
                          >
                            X
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : sortedTasks.length === 0 ? (
          <div style={{
            background: '#fff',
            padding: '40px',
            borderRadius: '8px',
            textAlign: 'center',
            color: '#666'
          }}>
            <p style={{ margin: 0, fontSize: '16px' }}>
              No tasks found matching the current filters.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {filteredTasks.some(task => task.status === 'done') && (
              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: '4px',
                padding: '12px 16px',
                marginBottom: '0',
                color: '#856404'
              }}>
                Task(s) marked as done, change status to verified if they are completed
              </div>
            )}
            {Object.entries(grouped).map(([groupName, groupTasks]) => (
              <div key={groupName}>
                {groupBy !== 'none' && (
                  <h2 style={{
                    margin: '0 0 12px 0',
                    fontSize: '18px',
                    paddingBottom: '8px',
                    borderBottom: '2px solid #ddd'
                  }}>
                    {groupName} ({groupTasks.length})
                  </h2>
                )}

                <div style={{
                  background: '#fff',
                  borderRadius: '8px',
                  overflow: 'visible',
                  border: '1px solid #ddd'
                }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '14px'
                  }}>
                    <thead>
                      <tr style={{ background: '#f8f9fa' }}>
                        <th
                          onClick={() => handleSort('room_number')}
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            userSelect: 'none',
                            borderBottom: '2px solid #ddd',
                            width: '120px'
                          }}
                        >
                          Room {sortColumn === 'room_number' && (sortDirection === 'asc' ? '▲' : '▼')}
                        </th>
                        <th
                          onClick={() => handleSort('description')}
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            userSelect: 'none',
                            borderBottom: '2px solid #ddd'
                          }}
                        >
                          Description {sortColumn === 'description' && (sortDirection === 'asc' ? '▲' : '▼')}
                        </th>
                        <th
                          style={{
                            padding: '12px',
                            textAlign: 'center',
                            fontWeight: 'bold',
                            borderBottom: '2px solid #ddd',
                            width: '70px'
                          }}
                        >
                          Photo
                        </th>
                        <th
                          onClick={() => handleSort('trade_name')}
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            userSelect: 'none',
                            borderBottom: '2px solid #ddd',
                            width: '160px'
                          }}
                        >
                          Assigned To {sortColumn === 'trade_name' && (sortDirection === 'asc' ? '▲' : '▼')}
                        </th>
                        <th
                          onClick={() => handleSort('due_date')}
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            userSelect: 'none',
                            borderBottom: '2px solid #ddd',
                            width: '120px'
                          }}
                        >
                          Due Date {sortColumn === 'due_date' && (sortDirection === 'asc' ? '▲' : '▼')}
                        </th>
                        <th
                          onClick={() => handleSort('created_at')}
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            userSelect: 'none',
                            borderBottom: '2px solid #ddd',
                            width: '120px'
                          }}
                        >
                          Created {sortColumn === 'created_at' && (sortDirection === 'asc' ? '▲' : '▼')}
                        </th>
                        <th
                          onClick={() => handleSort('status')}
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            userSelect: 'none',
                            borderBottom: '2px solid #ddd',
                            width: '110px'
                          }}
                        >
                          Status {sortColumn === 'status' && (sortDirection === 'asc' ? '▲' : '▼')}
                        </th>
                        <th
                          style={{
                            padding: '12px',
                            borderBottom: '2px solid #ddd',
                            width: '60px'
                          }}
                        >
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupTasks.map(task => (
                        <tr
                          key={task.id}
                          style={{
                            borderBottom: '1px solid #eee',
                            background: task.status === 'done' ? '#fff3cd' : 'transparent'
                          }}
                        >
                          {/* Room - Simple display only */}
                          <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>
                            <div>
                              <div style={{ fontWeight: 'bold' }}>{task.room_number}</div>
                              {task.room_name && (
                                <div style={{ fontSize: '12px', color: '#666' }}>{task.room_name}</div>
                              )}
                              {task.story_name && (
                                <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>{task.story_name}</div>
                              )}
                            </div>
                          </td>

                          {/* Description - Simple display only */}
                          <td style={{ padding: '8px 12px', verticalAlign: 'middle' }}>
                            <div style={{
                              fontSize: '14px',
                              lineHeight: '1.4',
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {task.description}
                            </div>
                          </td>

                          {/* Photo - Keep thumbnail with lightbox */}
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                            {task.photo_url ? (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                <div
                                  onClick={() => setLightboxImage(getTaskPhotoUrl(task.photo_url))}
                                  style={{
                                    width: '30px',
                                    height: '40px',
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    border: '1px solid #ddd'
                                  }}
                                >
                                  <img
                                    src={getTaskPhotoUrl(task.photo_url) || ''}
                                    alt="Task"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  <label style={{ cursor: 'pointer' }}>
                                    <input
                                      type="file"
                                      accept="image/jpeg,image/png,image/webp,image/heic,.heic"
                                      style={{ display: 'none' }}
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                          if (window.confirm('This will replace the existing photo. Continue?')) {
                                            handleTaskPhotoUpload(task.id, file);
                                          }
                                        }
                                        e.target.value = '';
                                      }}
                                    />
                                    <span style={{ fontSize: '10px', color: '#007bff', cursor: 'pointer' }}>Replace</span>
                                  </label>
                                  <button
                                    onClick={() => {
                                      if (window.confirm('Delete this photo?')) {
                                        handleDeleteTaskPhoto(task.id);
                                      }
                                    }}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      fontSize: '10px',
                                      color: '#dc3545',
                                      cursor: 'pointer',
                                      padding: 0
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <label style={{ cursor: 'pointer' }}>
                                <input
                                  type="file"
                                  accept="image/jpeg,image/png,image/webp,image/heic,.heic"
                                  style={{ display: 'none' }}
                                  disabled={uploadingPhotoTaskId === task.id}
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      handleTaskPhotoUpload(task.id, file);
                                    }
                                    e.target.value = '';
                                  }}
                                />
                                <span style={{
                                  padding: '4px 8px',
                                  background: '#f0f0f0',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  color: '#666'
                                }}>
                                  {uploadingPhotoTaskId === task.id ? '...' : '+ Add'}
                                </span>
                              </label>
                            )}
                          </td>

                          {/* Trade - Simple display only */}
                          <td style={{ padding: '8px 12px' }}>
                            {task.trade_name || '—'}
                          </td>

                          {/* Due Date - Simple display only */}
                          <td style={{ padding: '8px 12px' }}>
                            {task.due_date ? new Date(task.due_date).toLocaleDateString() : '—'}
                          </td>

                          {/* Created */}
                          <td style={{ padding: '12px' }}>
                            {task.created_at ? new Date(task.created_at).toLocaleDateString() : '—'}
                          </td>

                          {/* Status - Simple colored badge */}
                          <td style={{ padding: '8px 12px' }}>
                            <span style={{
                              padding: '6px 12px',
                              fontSize: '13px',
                              borderRadius: '4px',
                              background: getStatusColor(task.status),
                              color: '#fff',
                              fontWeight: 'bold',
                              display: 'inline-block'
                            }}>
                              {task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                            </span>
                          </td>

                          {/* Actions - Verify button + pencil icon for EditTaskModal */}
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                              {task.status === 'done' && (
                                <button
                                  onClick={() => handleVerifyTask(task.id)}
                                  disabled={verifyingTaskId === task.id}
                                  title="Verify task"
                                  style={{
                                    background: '#28a745',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '6px 12px',
                                    cursor: verifyingTaskId === task.id ? 'not-allowed' : 'pointer',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    opacity: verifyingTaskId === task.id ? 0.6 : 1
                                  }}
                                >
                                  {verifyingTaskId === task.id ? '...' : '✓ Verify'}
                                </button>
                              )}
                              <button
                                onClick={() => setEditingTask(task)}
                                title="Edit task"
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  padding: '4px 8px',
                                  cursor: 'pointer',
                                  fontSize: '18px',
                                  lineHeight: '1',
                                  color: '#999',
                                  transition: 'color 0.15s'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.color = '#007bff';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.color = '#999';
                                }}
                              >
                                ✎
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* CSV Import Pre-Upload Modal */}
      {importMode?.showPreUploadModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '20px', color: '#333' }}>
              Import Tasks from CSV
            </h3>

            <div style={{ marginBottom: '20px', fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
              <p style={{ margin: '0 0 12px 0' }}>
                Upload a CSV file with the following columns:
              </p>
              <ul style={{ margin: '0 0 12px 0', paddingLeft: '20px' }}>
                <li><strong>Room Number</strong> - Exact room number (blank = Whole House)</li>
                <li><strong>Room Name</strong> - Used if Room Number is blank</li>
                <li><strong>Description</strong> - Required</li>
                <li><strong>Trade</strong> - Trade name (fuzzy matched)</li>
                <li><strong>Due Date</strong> - Format: MM/DD/YYYY</li>
                <li><strong>Status</strong> - open, done, or verified (default: open)</li>
              </ul>
            </div>

            <button
              onClick={downloadSampleCSV}
              style={{
                background: '#28a745',
                color: '#fff',
                border: 'none',
                padding: '10px 20px',
                cursor: 'pointer',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 'bold',
                marginBottom: '20px',
                width: '100%'
              }}
            >
              Download Sample CSV
            </button>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                padding: '20px',
                border: '2px dashed #ccc',
                borderRadius: '8px',
                textAlign: 'center',
                cursor: 'pointer',
                background: '#f8f9fa'
              }}>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      processCSVFile(file);
                    }
                    e.target.value = '';
                  }}
                  style={{ display: 'none' }}
                />
                <div style={{ fontSize: '14px', color: '#666' }}>
                  <strong>Click to select a CSV file</strong>
                </div>
              </label>
            </div>

            <button
              onClick={handleCancelImport}
              style={{
                background: '#6c757d',
                color: '#fff',
                border: 'none',
                padding: '10px 20px',
                cursor: 'pointer',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 'bold',
                width: '100%'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* CSV Import Summary Modal */}
      {importMode?.showSummaryModal && importMode.summary && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', color: '#333' }}>
              Import Summary
            </h3>

            <div style={{ marginBottom: '20px', fontSize: '14px', lineHeight: '1.8' }}>
              <div style={{ color: '#28a745', fontWeight: 'bold', marginBottom: '8px' }}>
                {importMode.summary.imported} task{importMode.summary.imported !== 1 ? 's' : ''} ready to import
              </div>

              {importMode.summary.skipped.length > 0 && (
                <div style={{ color: '#dc3545', marginBottom: '8px' }}>
                  {importMode.summary.skipped.length} row{importMode.summary.skipped.length !== 1 ? 's' : ''} skipped (no description): rows {importMode.summary.skipped.map(s => s.row).join(', ')}
                </div>
              )}

              {importMode.summary.roomWarnings > 0 && (
                <div style={{ color: '#ffc107', marginBottom: '8px' }}>
                  {importMode.summary.roomWarnings} task{importMode.summary.roomWarnings !== 1 ? 's' : ''} with room warnings
                </div>
              )}

              {importMode.summary.tradeWarnings > 0 && (
                <div style={{ color: '#ffc107', marginBottom: '8px' }}>
                  {importMode.summary.tradeWarnings} task{importMode.summary.tradeWarnings !== 1 ? 's' : ''} with trade warnings
                </div>
              )}
            </div>

            {(importMode.summary.roomWarnings > 0 || importMode.summary.tradeWarnings > 0) && (
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '20px' }}>
                Tasks with warnings are highlighted in yellow. You can edit them before saving.
              </p>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleCancelImport}
                style={{
                  flex: 1,
                  background: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  padding: '12px 20px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => setImportMode(prev => prev ? { ...prev, isActive: true, showSummaryModal: false } : null)}
                disabled={importMode.summary.imported === 0}
                style={{
                  flex: 1,
                  background: importMode.summary.imported === 0 ? '#ccc' : '#007bff',
                  color: '#fff',
                  border: 'none',
                  padding: '12px 20px',
                  cursor: importMode.summary.imported === 0 ? 'not-allowed' : 'pointer',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                View Tasks
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox for task photos */}
      {lightboxImage && (
        <div
          onClick={() => setLightboxImage(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            cursor: 'pointer'
          }}
        >
          <img
            src={lightboxImage}
            alt="Task photo"
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain'
            }}
          />
          <button
            onClick={() => setLightboxImage(null)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255, 255, 255, 0.2)',
              color: '#fff',
              border: 'none',
              borderRadius: '50%',
              width: '30px',
              height: '40px',
              fontSize: '24px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Edit Task Modal */}
      <EditTaskModal
        task={editingTask}
        rooms={rooms}
        trades={trades}
        isOpen={editingTask !== null}
        onClose={() => setEditingTask(null)}
        onSave={async () => {
          if (!token || !projectId) return;
          const tasksData = await getProjectTasks(token, parseInt(projectId));
          setTasks(tasksData);
        }}
        onDelete={async () => {
          if (!token || !projectId) return;
          const tasksData = await getProjectTasks(token, parseInt(projectId));
          setTasks(tasksData);
        }}
        token={token || ''}
      />
    </div>
  );
}
